import React, { useContext, useMemo, useState, useRef, type ChangeEvent, type MouseEvent } from 'react';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { Context as AuthContext } from '../../../api/auth/context';
import messagingApi from '../../../api/messaging/api';
import { displayToast } from '../../RTToast';

import { useHeaderUserBoxStyles } from './styles/headerUserBox.styles';
import { resizeImage } from '../../../utils/resizeImage';
import UserMenu from './UserMenu';
import TableSettingsDialog from './TableSettingsDialog';
import ColorThemeDialog from './ColorThemeDialog';
import { generateNameInitials } from '../../../utils/getFullName';

function HeaderUserbox() {
    const { currUser, user, setUser } = useContext(AuthContext);

    const classes = useHeaderUserBoxStyles();
    const nameInitials = useMemo(() => generateNameInitials(currUser?.name), [currUser?.name]);
    const profilePic = currUser?.profilePicture;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [colorThemeOpen, setColorThemeOpen] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleUploadPicture = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            displayToast('error', 'Invalid file', 'Please select an image file.');
            return;
        }
        try {
            // 120 px for avatars; 1200 px for the contact card's "view photo" — 2x its 600 px display, for Retina screens
            const [base64, large] = await Promise.all([resizeImage(file, 120), resizeImage(file, 1200)]);
            await messagingApi.saveProfilePicture(base64, large);

            const updatedUsers = user.map((u) => {
                return u._id === currUser?._id ? { ...u, profilePicture: base64 } : u;
            });
            setUser(updatedUsers);

            displayToast('success', 'Saved', 'Profile picture updated.');
            handleClose();
        } catch (err) {
            console.error('Failed to upload picture:', err);
            displayToast('error', 'Error', 'Failed to upload picture.');
        }
        // Reset input so the same file can be re-selected
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    return (
        <>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleUploadPicture}
            />
            <Button
                color="inherit"
                onClick={handleClick}
                disableRipple
                className="btn-inverse"
                sx={{
                    textTransform: 'none',
                    borderRadius: '999px',
                    px: 0.5,
                    py: 0.25,
                    gap: 0.5,
                    minWidth: 0,
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sizes="44" className={`${classes.avatar} ${classes.avatarOnBar}`} src={profilePic || undefined}>
                        {!profilePic && nameInitials}
                    </Avatar>
                </Box>
                <KeyboardArrowDownIcon
                    sx={{
                        fontSize: 18,
                        opacity: 0.7,
                        transition: 'transform 180ms ease',
                        transform: anchorEl ? 'rotate(180deg)' : 'rotate(0deg)'
                    }}
                />
            </Button>

            <UserMenu
                anchorEl={anchorEl}
                onClose={handleClose}
                initials={nameInitials}
                onUploadClick={() => fileInputRef.current?.click()}
                onOpenSettings={() => setSettingsOpen(true)}
                onOpenColorTheme={() => setColorThemeOpen(true)}
            />

            <TableSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
            <ColorThemeDialog open={colorThemeOpen} onClose={() => setColorThemeOpen(false)} />
        </>
    );
}
export default React.memo(HeaderUserbox);
